import { useState } from "react";
import { syncApiUrl, getAuthToken } from "@cliqsuite/db";
import { DevicePic } from "@cliqsuite/ui";

interface DeviceRow {
  id: string;
  business_id: string;
  store_id: string;
  pairing_code: string;
  device_type: string;
  register_name: string;
  is_paired: boolean;
  bypass_geofence: boolean;
  device_name: string | null;
  last_seen_at: string | null;
}

/** "2 min ago", "3 hr ago", then the date. */
function ago(iso: string): string {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min} min ago`;
  if (min < 24 * 60) return `${Math.floor(min / 60)} hr ago`;
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function authHeaders() {
  return {
    "content-type": "application/json",
    ...(getAuthToken() ? { authorization: `Bearer ${getAuthToken()}` } : {}),
  };
}

/**
 * Platform-owner debugging tool — look up a specific till by its pairing
 * code and toggle core.devices.bypass_geofence, so you can sign into a
 * customer's POS remotely to reproduce a reported bug without being
 * physically at their store. Deliberately narrow (search-by-code, not a
 * full device browser) — this exists for occasional one-off debugging, not
 * fleet management. See services/sync-api/src/routes/core.ts's
 * bypassGeofenceGate for why this write is platform-scope-only server-side
 * regardless of what this page does.
 */
export function DevicesPage() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [device, setDevice] = useState<DeviceRow | null>(null);
  const [businessName, setBusinessName] = useState("");
  const [storeName, setStoreName] = useState("");
  const [toggling, setToggling] = useState(false);
  const [confirming, setConfirming] = useState(false);

  async function lookup() {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setLoading(true);
    setError("");
    setDevice(null);
    try {
      const res = await fetch(syncApiUrl(`/core/devices?pairing_code=${encodeURIComponent(trimmed)}`), { headers: authHeaders() });
      const body = (await res.json().catch(() => null)) as { rows?: DeviceRow[] } | null;
      const row = body?.rows?.[0];
      if (!res.ok || !row) {
        setError("No device has that pairing code.");
        return;
      }
      setDevice(row);
      const [bizRes, storeRes] = await Promise.all([
        fetch(syncApiUrl(`/core/businesses/${row.business_id}`), { headers: authHeaders() }),
        fetch(syncApiUrl(`/core/stores/${row.store_id}`), { headers: authHeaders() }),
      ]);
      const bizBody = (await bizRes.json().catch(() => null)) as { row?: { name: string } } | null;
      const storeBody = (await storeRes.json().catch(() => null)) as { row?: { name: string } } | null;
      setBusinessName(bizBody?.row?.name ?? "—");
      setStoreName(storeBody?.row?.name ?? "—");
    } catch {
      setError("Couldn't reach the server. Try again.");
    } finally {
      setLoading(false);
    }
  }

  async function toggleBypass() {
    if (!device) return;
    setToggling(true);
    try {
      const next = !device.bypass_geofence;
      const res = await fetch(syncApiUrl(`/core/devices/${device.id}`), {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ bypass_geofence: next }),
      });
      if (!res.ok) {
        setError("Couldn't update. Check you're signed in as a platform admin.");
        return;
      }
      setDevice({ ...device, bypass_geofence: next });
    } catch {
      setError("Couldn't reach the server — try again.");
    } finally {
      setToggling(false);
    }
  }

  const TYPE: Record<string, { icon: string; label: string }> = {
    pos: { icon: "point_of_sale", label: "POS register" },
    kds: { icon: "soup_kitchen", label: "Kitchen screen" },
    services: { icon: "spa", label: "Service terminal" },
  };
  const t = device ? (TYPE[device.device_type] ?? { icon: "devices", label: device.device_type }) : null;

  return (
    <div>
      <section className="o-card">
        <form
          style={{ display: "flex", gap: 10, alignItems: "flex-end" }}
          onSubmit={(e) => {
            e.preventDefault();
            void lookup();
          }}
        >
          <label className="o-ifield" style={{ flex: 1 }}>
            <span className="o-field__label">Pairing code</span>
            <span className="o-ifield__box">
              <span className="ms" aria-hidden="true">search</span>
              <input aria-label="Pairing code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="BREW-1124" style={{ textTransform: "uppercase", letterSpacing: 1 }} />
            </span>
          </label>
          <button type="submit" className="o-btn o-btn--ink" style={{ height: 48 }} disabled={loading || !code.trim()}>
            {loading ? "Looking up…" : "Look up"}
          </button>
        </form>
        {error && <div className="o-error">{error}</div>}
      </section>

      {device && t && (
        <section className="o-card">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <DevicePic kind={t.icon === "point_of_sale" ? "till" : t.icon === "soup_kitchen" ? "wall" : "tablet"} state={device.is_paired ? "on" : "unpaired"} icon={t.icon} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>{device.register_name}</div>
              <div className="o-row__sub" style={{ fontSize: 13 }}>
                {t.label}, {device.pairing_code}
              </div>
            </div>
            <span className={`o-pill ${device.is_paired ? "o-pill--success" : "o-pill--warning"}`}>{device.is_paired ? "Paired" : "Waiting to pair"}</span>
          </div>
          <div className="o-mini-stats" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))", marginTop: 16 }}>
            <div>
              <span style={{ marginTop: 0 }}>Business</span>
              <strong style={{ fontSize: 15, marginTop: 3 }}>{businessName}</strong>
            </div>
            <div>
              <span style={{ marginTop: 0 }}>Store</span>
              <strong style={{ fontSize: 15, marginTop: 3 }}>{storeName}</strong>
            </div>
            <div>
              <span style={{ marginTop: 0 }}>Last seen</span>
              <strong style={{ fontSize: 15, marginTop: 3 }}>{device.last_seen_at ? ago(device.last_seen_at) : "Never"}</strong>
            </div>
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 16, padding: "14px 16px", borderRadius: 16, border: "1.5px solid var(--amber-soft)" }}>
            <span className="ms" aria-hidden="true" style={{ fontSize: 24, color: "var(--amber)" }}>wrong_location</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>Bypass geofence</div>
              <div className="o-row__sub" style={{ whiteSpace: "normal", marginTop: 2 }}>Lets staff sign in here from anywhere. For support only, turn it off after.</div>
            </div>
            <span className={`o-pill ${device.bypass_geofence ? "o-pill--warning" : ""}`}>{device.bypass_geofence ? "On" : "Off"}</span>
            <button type="button" className={`o-btn ${device.bypass_geofence ? "o-btn--grey" : "o-btn--ink"}`} disabled={toggling} onClick={() => (device.bypass_geofence ? void toggleBypass() : setConfirming(true))}>
              {toggling ? "Saving…" : device.bypass_geofence ? "Turn off" : "Turn on"}
            </button>
          </div>
        </section>
      )}

      {confirming && device && (
        <div className="o-scrim o-dialog--center" style={{ zIndex: 140 }} onClick={() => setConfirming(false)}>
          <section className="o-dialog" role="alertdialog" aria-modal="true" aria-label="Bypass the geofence?" onClick={(e) => e.stopPropagation()}>
            <div className="o-dialog__body" style={{ paddingTop: 24 }}>
              <span className="o-auth__icon is-amber"><span className="ms" aria-hidden="true">wrong_location</span></span>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Bypass the geofence?</h2>
              <p className="o-row__sub" style={{ whiteSpace: "normal", fontSize: 14, marginTop: 6 }}>
                {device.register_name} at {businessName} can sign in from any location until you turn this off. It's logged in the audit log.
              </p>
            </div>
            <footer className="o-dialog__foot">
              <button type="button" className="o-btn o-btn--grey" style={{ flex: 1 }} onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="o-btn o-btn--ink"
                style={{ flex: 1 }}
                onClick={() => {
                  setConfirming(false);
                  void toggleBypass();
                }}
              >
                Turn on
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}
